/**
 * LoreEditor — DM-only split-pane editor for a single lore note.
 *
 * When mounted, takes over the centre + right columns of the lore page
 * (the parent grid switches to two columns). Layout is:
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Toolbar: status │ wrap btns │ frontmatter toggle │ actions │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ (collapsible frontmatter bar)                           │
 *   ├────────────────────────┬─────────────────────────────────┤
 *   │ Editor (line numbers + │ Live rendered preview           │
 *   │ diff gutter + textarea)│ (additions green, deletions red │
 *   │                        │  vs published vault copy)       │
 *   └────────────────────────┴─────────────────────────────────┘
 *
 * Editing surfaces (unchanged from before):
 *
 *   1. A plain `<textarea>` for the body. Debounced autosave to a draft in
 *      DynamoDB on every keystroke; no auto-write to S3 ever — only the
 *      explicit Publish button promotes a draft to the vault.
 *
 *   2. A frontmatter form (visibility, reveal date, summary, tags). Each
 *      field calls `transformFrontmatter` on the server, which uses the
 *      Domain-layer line-based YAML editor — comments, ordering, quoting,
 *      and unknown keys are preserved.
 *
 *   3. A selection-based section toolbar. The DM selects text in the body
 *      textarea and clicks "Wrap in players block" or "Wrap in reveal block".
 *
 * Conflict handling: both `putNote` and `publishDraft` throw `LoreConflictError`
 * on a 412 from S3 (vault changed under us). We catch it, surface the current
 * vault content, and let the DM pick "use mine" / "use vault".
 *
 * The diff gutter compares the current editor buffer against the published
 * vault copy (`source.content`). Green markers = added/modified lines,
 * red markers = deleted lines. The preview pane renders a live markdown
 * preview of the current editor text.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { marked } from 'marked';
import {
  deleteDraft,
  FrontmatterPatch,
  getDraft,
  getNoteSource,
  LoreConflict,
  LoreConflictError,
  LoreSource,
  publishDraft,
  putDraft,
  putNote,
  SectionWrap,
  transformFrontmatter,
  transformSection,
} from '../lib/loreApi';
import { lineDiff, type DiffLine } from '../lib/lineDiff';
import './LoreEditor.css';

const SYNC_DEBOUNCE_MS = 30_000;

interface Props {
  slug: string | null;
  knownPlayers: string[];
  onPublished: () => void;
  /** Called when the DM clicks "Close editor" — parent toggles edit mode off. */
  onClose: () => void;
}

interface FrontmatterParsed {
  raw: string;
  body: string;
  frontmatter: {
    players?: string[];
    playersAll?: boolean;
    playersNone?: boolean;
    reveal?: string;
    summary?: string;
    tags?: string[];
  };
}

function parseSource(raw: string): FrontmatterParsed {
  const m = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!m) {
    return { raw, body: raw, frontmatter: {} };
  }
  const yaml = m[1];
  const body = m[2];
  const fm: FrontmatterParsed['frontmatter'] = {};

  for (const line of yaml.split('\n')) {
    const idx = line.indexOf(':');
    if (idx < 0) continue;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (!key) continue;
    if (key === 'players') {
      if (value === 'all') { fm.playersAll = true; continue; }
      if (value === '[]' || value === '') { fm.playersNone = true; continue; }
      if (value.startsWith('[') && value.endsWith(']')) {
        const inner = value.slice(1, -1);
        fm.players = inner
          .split(',')
          .map((s) => s.trim().replace(/^["']|["']$/g, ''))
          .filter((s) => s.length > 0);
        continue;
      }
      fm.players = [value.replace(/^["']|["']$/g, '')];
    } else if (key === 'reveal') {
      fm.reveal = value;
    } else if (key === 'summary') {
      fm.summary = value.replace(/^["']|["']$/g, '');
    } else if (key === 'tags') {
      if (value.startsWith('[') && value.endsWith(']')) {
        const inner = value.slice(1, -1);
        fm.tags = inner
          .split(',')
          .map((s) => s.trim().replace(/^["']|["']$/g, ''))
          .filter((s) => s.length > 0);
      } else if (value) {
        fm.tags = [value];
      }
    }
  }

  return { raw, body, frontmatter: fm };
}

function offsetToLine(body: string, offset: number): number {
  let line = 0;
  for (let i = 0; i < offset && i < body.length; i++) {
    if (body[i] === '\n') line++;
  }
  return line;
}

function splitFrontmatter(raw: string): { prefix: string; body: string } {
  const m = raw.match(/^---\n[\s\S]*?\n---\n?/);
  if (!m) return { prefix: '', body: raw };
  return { prefix: m[0], body: raw.slice(m[0].length) };
}

export default function LoreEditor({ slug, knownPlayers, onPublished, onClose }: Props) {
  const [source, setSource] = useState<LoreSource | null>(null);
  const [text, setText] = useState<string>('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<LoreConflict | null>(null);
  const [showFrontmatter, setShowFrontmatter] = useState(false);
  // Tracks the DM's dropdown intent independently of the parsed frontmatter.
  // Without this, selecting "Specific players…" with no players ticked writes
  // `players: []`, which the parser reads as `playersNone`, which hides the
  // checkboxes and snaps the dropdown back — a catch-22. This state breaks
  // the cycle: the checkboxes stay visible as long as the DM chose "list".
  // Reset to null on slug change so we derive from frontmatter on first load.
  const [visOverride, setVisOverride] = useState<'list' | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const gutterRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<number | null>(null);
  const textOwnerRef = useRef<string | null>(null);
  const lastObservedETagAtRef = useRef<number | null>(null);

  const waitForSyncWindow = useCallback(async () => {
    const observed = lastObservedETagAtRef.current;
    if (observed == null) return;
    const delta = Date.now() - observed;
    if (delta >= SYNC_DEBOUNCE_MS) return;
    const ago = Math.max(1, Math.round(delta / 1000));
    const wait = SYNC_DEBOUNCE_MS - delta;
    setBusy(`Obsidian synced this note ${ago}s ago, waiting…`);
    await new Promise<void>((resolve) => window.setTimeout(resolve, wait));
  }, []);

  // Load source + any existing draft when slug changes.
  useEffect(() => {
    if (!slug) {
      setSource(null);
      setText('');
      setHasDraft(false);
      setError(null);
      setConflict(null);
      setSavedAt(null);
      setVisOverride(null);
      textOwnerRef.current = null;
      lastObservedETagAtRef.current = null;
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setError(null);
    setConflict(null);

    (async () => {
      try {
        const src = await getNoteSource(slug);
        if (cancelled) return;
        setSource(src);
        lastObservedETagAtRef.current = Date.now();

        const draft = await getDraft(slug);
        if (cancelled) return;
        if (draft) {
          setText(draft.content);
          setHasDraft(true);
          setSavedAt(draft.updatedAt);
        } else {
          setText(src.content);
          setHasDraft(false);
          setSavedAt(null);
        }
        textOwnerRef.current = slug;
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [slug]);

  const scheduleAutosave = useCallback(
    (nextText: string) => {
      if (!slug || !source) return;
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(async () => {
        debounceRef.current = null;
        if (textOwnerRef.current !== slug) return;
        try {
          const result = await putDraft(slug, nextText, source.etag);
          setHasDraft(true);
          setSavedAt(result.savedAt);
          lastObservedETagAtRef.current = Date.now();
          setError(null);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }, 1000);
    },
    [slug, source],
  );

  const onTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setText(e.target.value);
      scheduleAutosave(e.target.value);
    },
    [scheduleAutosave],
  );

  const replaceText = useCallback(
    (next: string) => {
      setText(next);
      scheduleAutosave(next);
    },
    [scheduleAutosave],
  );

  const fm = useMemo(() => parseSource(text), [text]);

  // Diff between published source and current editor buffer.
  const diff: DiffLine[] = useMemo(() => {
    if (!source) return [];
    return lineDiff(source.content, text);
  }, [source, text]);

  // Build gutter data: one entry per visual row.
  const gutterData = useMemo(() => {
    return diff.map((d) => ({
      kind: d.kind,
      lineNo: d.currentLineNo,
    }));
  }, [diff]);

  // Sync gutter scroll with textarea scroll.
  const onTextareaScroll = useCallback(() => {
    const ta = textareaRef.current;
    const gutter = gutterRef.current;
    if (ta && gutter) {
      gutter.scrollTop = ta.scrollTop;
    }
  }, []);

  // Live markdown preview of the current editor text.
  const previewHtml = useMemo(() => {
    if (!text) return '';
    // Strip frontmatter for rendering — the preview shows the body only.
    const { body } = splitFrontmatter(text);
    return marked.parse(body, { async: false }) as string;
  }, [text]);

  const applyFrontmatter = useCallback(
    async (patch: FrontmatterPatch) => {
      if (!slug || !source) return;
      setBusy('Applying frontmatter…');
      setError(null);
      try {
        const r = await transformFrontmatter(text, patch);
        replaceText(r.body);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [slug, source, text, replaceText],
  );

  const wrapSelection = useCallback(
    async (kind: 'players' | 'reveal') => {
      if (!slug || !source) return;
      const ta = textareaRef.current;
      if (!ta) return;
      const { prefix, body } = splitFrontmatter(text);
      const start = Math.max(0, ta.selectionStart - prefix.length);
      const end = Math.max(start, ta.selectionEnd - prefix.length);
      if (end <= start) {
        setError('Select some text to wrap first.');
        return;
      }
      const startLine = offsetToLine(body, start);
      const endLine = offsetToLine(body, Math.max(start, end - 1));

      let section: SectionWrap;
      if (kind === 'players') {
        const list = window.prompt(
          'Players (comma-separated, or leave blank for "all"):',
          '',
        );
        if (list === null) return;
        section = {
          wrapKind: 'players',
          startLine,
          endLine,
          playersList: list.trim() || undefined,
        };
      } else {
        const date = window.prompt('Reveal date (YYYY-MM-DD):', '');
        if (date === null) return;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
          setError('Reveal date must be YYYY-MM-DD.');
          return;
        }
        section = {
          wrapKind: 'reveal',
          startLine,
          endLine,
          revealDate: date.trim(),
        };
      }

      setBusy('Wrapping section…');
      setError(null);
      try {
        const r = await transformSection(body, section);
        replaceText(prefix + r.body);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [slug, source, text, replaceText],
  );

  const onPublish = useCallback(async () => {
    if (!slug || !source) return;
    setError(null);
    setConflict(null);
    await waitForSyncWindow();
    setBusy('Publishing…');
    try {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      await putDraft(slug, text, source.etag);
      setHasDraft(true);

      await publishDraft(slug);
      const fresh = await getNoteSource(slug);
      setSource(fresh);
      setText(fresh.content);
      setHasDraft(false);
      setSavedAt(null);
      lastObservedETagAtRef.current = Date.now();
      onPublished();
    } catch (e) {
      if (e instanceof LoreConflictError) {
        setConflict(e.conflict);
        lastObservedETagAtRef.current = Date.now();
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  }, [slug, source, text, onPublished, waitForSyncWindow]);

  const onSaveDirect = useCallback(async () => {
    if (!slug || !source) return;
    setError(null);
    setConflict(null);
    await waitForSyncWindow();
    setBusy('Saving to vault…');
    try {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      await putNote(slug, text, source.etag);
      const fresh = await getNoteSource(slug);
      setSource(fresh);
      setText(fresh.content);
      setHasDraft(false);
      setSavedAt(null);
      lastObservedETagAtRef.current = Date.now();
      onPublished();
    } catch (e) {
      if (e instanceof LoreConflictError) {
        setConflict(e.conflict);
        lastObservedETagAtRef.current = Date.now();
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  }, [slug, source, text, onPublished, waitForSyncWindow]);

  const onDiscardDraft = useCallback(async () => {
    if (!slug || !source) return;
    if (!window.confirm('Discard the draft and revert to the vault copy?')) return;
    setBusy('Discarding draft…');
    setError(null);
    try {
      await deleteDraft(slug);
      setText(source.content);
      setHasDraft(false);
      setSavedAt(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [slug, source]);

  const onAcceptVaultConflict = useCallback(() => {
    if (!conflict) return;
    setText(conflict.currentBody);
    if (source) {
      setSource({ ...source, etag: conflict.currentEtag, content: conflict.currentBody });
    }
    setConflict(null);
  }, [conflict, source]);

  if (!slug) {
    return (
      <section className="lore-editor lore-editor--empty">
        <p className="lore-meta__hint">Select a note to edit.</p>
      </section>
    );
  }

  return (
    <section className="lore-editor">
      {/* ── Top toolbar ── */}
      <header className="lore-editor__topbar">
        <div className="lore-editor__topbar-left">
          <button
            type="button"
            className="lore-editor__close"
            onClick={onClose}
            title="Close editor"
          >
            ✕
          </button>
          <h3>Editing: {slug}</h3>
          <div className="lore-editor__status">
            {loading && <span>Loading…</span>}
            {!loading && hasDraft && <span className="lore-editor__draft-badge">draft</span>}
            {!loading && savedAt && (
              <span className="lore-editor__saved">
                saved {new Date(savedAt).toLocaleTimeString()}
              </span>
            )}
            {busy && <span className="lore-editor__busy">{busy}</span>}
          </div>
        </div>
        <div className="lore-editor__topbar-right">
          <button
            type="button"
            disabled={!!busy}
            onClick={() => wrapSelection('players')}
            title="Wrap selected text in %%players%% block"
          >
            Wrap players
          </button>
          <button
            type="button"
            disabled={!!busy}
            onClick={() => wrapSelection('reveal')}
            title="Wrap selected text in %%reveal%% block"
          >
            Wrap reveal
          </button>
          <span className="lore-editor__topbar-sep" />
          <button
            type="button"
            className={showFrontmatter ? 'lore-editor__fm-toggle--active' : ''}
            onClick={() => setShowFrontmatter((s) => !s)}
          >
            {showFrontmatter ? '▾ Frontmatter' : '▸ Frontmatter'}
          </button>
          <span className="lore-editor__topbar-sep" />
          {hasDraft && (
            <button
              type="button"
              disabled={!!busy}
              onClick={onDiscardDraft}
              title="Throw away the draft and reload from the vault"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            disabled={!!busy}
            onClick={onSaveDirect}
            title="Skip the draft and write straight to the vault"
          >
            Save direct
          </button>
          <button
            type="button"
            className="lore-editor__publish"
            disabled={!!busy}
            onClick={onPublish}
            title="Save current draft and publish to the vault"
          >
            Publish
          </button>
        </div>
      </header>

      {/* ── Errors / conflict banner ── */}
      {loadError && <div className="lore-error">{loadError}</div>}
      {error && <div className="lore-error">{error}</div>}

      {conflict && (
        <div className="lore-editor__conflict">
          <strong>Vault changed under you.</strong>
          <p>
            The vault copy was modified after your draft was loaded. Choose how to resolve:
          </p>
          <div className="lore-editor__conflict-actions">
            <button type="button" onClick={onAcceptVaultConflict}>
              Replace editor with vault copy
            </button>
            <button type="button" onClick={() => setConflict(null)}>
              Keep editing my version
            </button>
          </div>
        </div>
      )}

      {/* ── Collapsible frontmatter bar ── */}
      {showFrontmatter && source && !loadError && (
        <div className="lore-editor__frontmatter-bar">
          <label>
            <span>Visibility</span>
            <select
              value={
                // visOverride holds the dropdown at "list" even when the parsed
                // frontmatter has no players yet (which would otherwise read as
                // "none" or "default" and hide the checkboxes).
                visOverride === 'list'
                  ? 'list'
                  : fm.frontmatter.playersAll
                    ? 'all'
                    : fm.frontmatter.players && fm.frontmatter.players.length > 0
                      ? 'list'
                      : fm.frontmatter.playersNone
                        ? 'none'
                        : 'default'
              }
              disabled={!!busy}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'list') {
                  // Set the override so checkboxes appear immediately, even
                  // before any player is ticked. Don't write frontmatter yet —
                  // that happens when the DM ticks a checkbox.
                  setVisOverride('list');
                } else {
                  setVisOverride(null);
                  if (v === 'default') {
                    void applyFrontmatter({ setPlayers: true, players: null });
                  } else if (v === 'all') {
                    void applyFrontmatter({ setPlayers: true, players: { all: true } });
                  } else if (v === 'none') {
                    void applyFrontmatter({ setPlayers: true, players: { none: true } });
                  }
                }
              }}
            >
              <option value="default">DM only (default)</option>
              <option value="all">All players</option>
              <option value="none">DM only (explicit)</option>
              <option value="list">Specific players…</option>
            </select>
          </label>

          {/* Show player checkboxes when:
              - visOverride is 'list' (DM just picked "Specific players" from dropdown), OR
              - frontmatter already has a non-empty player list */}
          {(visOverride === 'list' || (fm.frontmatter.players !== undefined && fm.frontmatter.players.length > 0 && !fm.frontmatter.playersAll)) && (
            <div className="lore-editor__players-inline">
              {knownPlayers.map((p) => {
                const checked = fm.frontmatter.players?.includes(p) ?? false;
                return (
                  <label key={p}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!!busy}
                      onChange={(e) => {
                        const current = new Set(fm.frontmatter.players ?? []);
                        if (e.target.checked) current.add(p);
                        else current.delete(p);
                        void applyFrontmatter({
                          setPlayers: true,
                          players: { allowed: [...current] },
                        });
                        // Clear the override once a real player list exists in
                        // frontmatter — the parsed state can drive from here.
                        if (current.size > 0) setVisOverride(null);
                      }}
                    />
                    {p}
                  </label>
                );
              })}
              {knownPlayers.length === 0 && (
                <span className="lore-meta__hint">No players configured.</span>
              )}
            </div>
          )}

          <label>
            <span>Reveal</span>
            <input
              type="date"
              value={fm.frontmatter.reveal ?? ''}
              disabled={!!busy}
              onChange={(e) =>
                void applyFrontmatter({
                  setReveal: true,
                  reveal: e.target.value || null,
                })
              }
            />
          </label>

          <label>
            <span>Summary</span>
            <input
              type="text"
              key={`summary:${slug}:${fm.frontmatter.summary ?? ''}`}
              defaultValue={fm.frontmatter.summary ?? ''}
              disabled={!!busy}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next === (fm.frontmatter.summary ?? '')) return;
                void applyFrontmatter({
                  setSummary: true,
                  summary: next || null,
                });
              }}
            />
          </label>

          <label>
            <span>Tags</span>
            <input
              type="text"
              disabled={!!busy}
              defaultValue={(fm.frontmatter.tags ?? []).join(', ')}
              key={(fm.frontmatter.tags ?? []).join(',')}
              placeholder="comma-separated"
              onBlur={(e) => {
                const tags = e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0);
                void applyFrontmatter({
                  setTags: true,
                  tags: tags.length === 0 ? null : tags,
                });
              }}
            />
          </label>
        </div>
      )}

      {/* ── Split pane: editor + preview ── */}
      {source && !loadError && (
        <div className="lore-editor__split">
          {/* Left pane: line-numbered editor with diff gutter */}
          <div className="lore-editor__pane lore-editor__pane--edit">
            <div className="lore-editor__pane-label">
              <span>Source</span>
              <span className="lore-editor__pane-hint">{text.split('\n').length} lines</span>
            </div>
            <div className="lore-editor__editor-wrap">
              <div
                ref={gutterRef}
                className="lore-editor__gutter"
                aria-hidden="true"
              >
                {gutterData.map((entry, i) => (
                  <div
                    key={i}
                    className={`lore-editor__gutter-line lore-editor__gutter-line--${entry.kind}`}
                  >
                    {entry.kind === 'removed' ? (
                      <span className="lore-editor__gutter-removed-marker">−</span>
                    ) : (
                      <>
                        <span className="lore-editor__gutter-indicator">
                          {entry.kind === 'added' ? '+' : ' '}
                        </span>
                        <span className="lore-editor__gutter-num">{entry.lineNo}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <textarea
                ref={textareaRef}
                className="lore-editor__textarea"
                value={text}
                onChange={onTextChange}
                onScroll={onTextareaScroll}
                spellCheck={false}
                disabled={!!busy && busy !== 'Applying frontmatter…' && busy !== 'Wrapping section…'}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="lore-editor__divider" />

          {/* Right pane: live rendered preview */}
          <div className="lore-editor__pane lore-editor__pane--preview">
            <div className="lore-editor__pane-label">
              <span>Preview</span>
              {source && text !== source.content && (
                <span className="lore-editor__pane-hint lore-editor__pane-hint--modified">modified</span>
              )}
            </div>
            <div className="lore-editor__preview-wrap">
              <article
                className="lore-body"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
